/**
 * The account id typed when creating an account (CustomerNew, also opened as a
 * popup from the quote screen).
 *
 * WHY THIS EXISTS
 * The id is optional. Left empty, the engine assigns ACT-<sequence>
 * (PGAccountService.create). A typed id is saved exactly as sent, and nothing
 * between this form and the database checks it:
 *
 *   - parseObjToString (utils.js) puts the value between quotes without escaping
 *     it, so a quote, a backslash or a line break makes the GraphQL request fail;
 *   - a TAB passes GraphQL and is saved. core_engine.insert_jobs then builds each
 *     job payload as '{"accountId": "' || accountid || '"}' and casts it to json,
 *     which fails on the raw TAB and stops that job for every account in the run.
 *     jasec-preprod, 2 Oct 2026: 'JASEC-20260920-001' pasted from a spreadsheet
 *     with a TAB at the end stopped the 10 Sep billing run;
 *   - after saving, the screen opens /customers/<id>/info without encoding the id,
 *     so '/', '?', '#' or a space break the route.
 *
 * THE RULE
 * Whitespace around the id (spaces, TAB, CR/LF, no-break space) is dropped while
 * typing and again on submit. What is left must be 1 to 50 characters from A-Z,
 * a-z, 0-9, '-' and '_'. Every account id on jasec prod, jasec-preprod and
 * jasec-dev fits, except QA test ids built from the characters this rule blocks.
 * Typed ids run to 18 characters ('JASEC-20260920-001') and the narrowest database
 * column holding an account id is varchar(100), so 50 is a safe ceiling.
 *
 * Characters are rejected, never removed: 'JASEC-2026092|-002' is a typo, and
 * silently turning it into 'JASEC-2026092-002' would save a different id. For the
 * same reason the input carries no maxlength. A browser cuts a paste down to
 * maxlength before the screen sees it, so a 60-character id would be saved as its
 * first 50 characters with no message, and a TAB pasted in front of a 50-character
 * id would cost the id its last character. The pattern and the submit check refuse
 * an id that is too long instead.
 */

/** The longest id the rule accepts. Enforced by the rule, not by a maxlength. */
export const ACCOUNT_ID_MAX_LENGTH = 50;

/** The rule, for code. */
export const ACCOUNT_ID_REGEX = new RegExp(`^[A-Za-z0-9_-]{1,${ACCOUNT_ID_MAX_LENGTH}}$`);

/**
 * The same rule for <input pattern="">, which marks the field red once the form is
 * validated. Browsers compile that attribute with the regex `v` flag, where a bare
 * '-' inside [...] is a syntax error and the browser silently ignores the whole
 * pattern, so the hyphen is escaped here. The browser adds the anchors itself.
 */
export const ACCOUNT_ID_HTML_PATTERN = `[A-Za-z0-9_\\-]{1,${ACCOUNT_ID_MAX_LENGTH}}`;

/** The id as it will be sent: surrounding whitespace removed, '' when nothing is left. */
export const normalizeAccountId = value => (value === null || value === undefined ? '' : String(value).trim());

/** True when the id is empty (the engine assigns one) or follows the rule. */
export const isValidAccountId = value => {
  const id = normalizeAccountId(value);
  return id === '' || ACCOUNT_ID_REGEX.test(id);
};

export default isValidAccountId;
